import { get, post, patch, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'automunity';

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) {
      console.warn('No authentication token found');
      return {};
    }
    return { Authorization: token };
  } catch (error) {
    console.warn('No auth session found:', error);
    return {};
  }
};

export const automunityAPI = {
  // Automations
  getAutomations: async () => {
    const headers = await getAuthHeaders();
    return get({ 
      apiName: API_NAME, 
      path: '/automations', 
      options: { headers } 
    }).response;
  },
  getAutomation: async (id: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${id}`, options: { headers } }).response;
  },
  createAutomation: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/automations', options: { body: data, headers } }).response;
  },
  updateAutomation: async (id: string, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/automations/${id}`, options: { body: data, headers } }).response;
  },
  deleteAutomation: async (id: string) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/automations/${id}`, options: { headers } }).response;
  },

  // Teams
  getTeams: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/teams', options: { headers } }).response;
  },
  createTeam: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/teams', options: { body: data, headers } }).response;
  },
  deleteTeam: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/teams/${id}`, options: { headers } }).response;
  },
  updateTeam: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/teams/${id}`, options: { body: data, headers } }).response;
  },

  // Automation Teams (multi-team junction)
  getAutomationTeams: async (automationId: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${automationId}/teams`, options: { headers } }).response;
  },
  addAutomationTeam: async (automationId: string, teamId: number, isPrimary: boolean = false) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/${automationId}/teams`, options: { body: { team_id: teamId, is_primary: isPrimary ? 1 : 0 }, headers } }).response;
  },
  removeAutomationTeam: async (automationId: string, teamId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/automations/${automationId}/teams/${teamId}`, options: { headers } }).response;
  },

  // Services
  getServices: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/services', options: { headers } }).response;
  },
  createService: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/services', options: { body: data, headers } }).response;
  },
  deleteService: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/services/${id}`, options: { headers } }).response;
  },
  updateService: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/services/${id}`, options: { body: data, headers } }).response;
  },

  // Stages
  getStages: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/stages', options: { headers } }).response;
  },
  createStage: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/stages', options: { body: data, headers } }).response;
  },
  deleteStage: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/stages/${id}`, options: { headers } }).response;
  },
  updateStage: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/stages/${id}`, options: { body: data, headers } }).response;
  },

  // Badges
  getBadges: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/users/badges', options: { headers, queryParams: { table: 'badge' } } }).response;
  },
  getUserBadges: async (userId: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/users/badges', options: { headers, queryParams: { sqlQueryParams: JSON.stringify({ user_id: userId }) } } }).response;
  },
  createBadge: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/users/badges', options: { body: data, headers } }).response;
  },
  deleteBadge: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/users/badges/${id}`, options: { headers } }).response;
  },
  updateBadge: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/users/badges/${id}`, options: { body: data, headers } }).response;
  },

  // Roles
  getRoles: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/roles', options: { headers } }).response;
  },
  createRole: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/roles', options: { body: data, headers } }).response;
  },
  deleteRole: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/roles/${id}`, options: { headers } }).response;
  },
  updateRole: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/roles/${id}`, options: { body: data, headers } }).response;
  },

  // Hosts
  getHosts: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/hosts', options: { headers } }).response;
  },
  createHost: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/hosts', options: { body: data, headers } }).response;
  },
  deleteHost: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/hosts/${id}`, options: { headers } }).response;
  },
  updateHost: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/hosts/${id}`, options: { body: data, headers } }).response;
  },

  // Access Control — current user's role & permissions
  getMyAccess: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/users/me/access', options: { headers } }).response;
  },
  updateUserAccessRole: async (userId: string, accessRole: string) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/users/${userId}`, options: { body: { access_role: accessRole }, headers } }).response;
  },

  // Users
  getUsers: async (params?: { include_inactive?: boolean }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.include_inactive) queryParams.include_inactive = 'true';
    return get({ apiName: API_NAME, path: '/users', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },
  getUser: async (id: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/users/${id}`, options: { headers } }).response;
  },
  createUser: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/users', options: { body: data, headers } }).response;
  },
  updateUser: async (id: string, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/users/${id}`, options: { body: data, headers } }).response;
  },

  // Contributions
  getContributions: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/automations/contributions', options: { headers } }).response;
  },
  getAutomationContributions: async (id: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${id}/contributions`, options: { headers } }).response;
  },
  createContribution: async (automationId: string, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/${automationId}/contributions`, options: { body: data, headers } }).response;
  },
  deleteContribution: async (automationId: string, contributionId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/automations/${automationId}/contributions/${contributionId}`, options: { headers } }).response;
  },

  // Automation Services
  addAutomationService: async (automationId: string, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/${automationId}/services`, options: { body: data, headers } }).response;
  },
  deleteAutomationService: async (automationId: string, serviceId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/automations/${automationId}/services/${serviceId}`, options: { headers } }).response;
  },

  // Automation Stages
  addAutomationStage: async (automationId: string, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/${automationId}/stages`, options: { body: data, headers } }).response;
  },
  getAutomationStages: async (automationId: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${automationId}/stages`, options: { headers } }).response;
  },
  getAutomationServices: async (automationId: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${automationId}/services`, options: { headers } }).response;
  },

  // Invocations
  getInvocations: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/automations/invocations', options: { headers } }).response;
  },
  getAutomationInvocations: async (id: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${id}/invocations`, options: { headers } }).response;
  },

  // Integration configs (Lambda functions linked to automations)
  getIntegrationConfigs: async (automationId: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/integration-configs', options: { headers, queryParams: { sqlQueryParams: JSON.stringify({ automationId }) } } }).response;
  },

  // Changelog
  getChangelog: async (automationId?: string, limit: number = 50) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = { limit: String(limit) };
    if (automationId) queryParams.automation_id = automationId;
    return get({ apiName: API_NAME, path: '/changelogs', options: { headers, queryParams } }).response;
  },

  // Monitor (AWS Integration Add-on)
  getMonitoredFunctions: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/monitor/functions', options: { headers } }).response;
  },
  getMonitorSnapshots: async (functionName: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/monitor/snapshots`, options: { headers, queryParams: { function_name: functionName } } }).response;
  },
  getMonitorAlerts: async (params?: { status?: string; severity?: string; assigned_to?: string }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.severity) queryParams.severity = params.severity;
    if (params?.assigned_to) queryParams.assigned_to = params.assigned_to;
    return get({ apiName: API_NAME, path: '/monitor/alerts', options: { headers, queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined } }).response;
  },
  resolveAlert: async (alertId: number) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/monitor/alerts/${alertId}`, options: { body: { status: 'resolved' }, headers } }).response;
  },
  updateAlert: async (alertId: number, updates: { status?: string; assigned_to?: number | null; notes?: string; resolved_by?: number }) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/monitor/alerts/${alertId}`, options: { body: updates, headers } }).response;
  },
  bulkUpdateAlerts: async (alertIds: number[], updates: { status?: string; assigned_to?: number | null; action?: string }) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/monitor/alerts/manage', options: { body: { _action: 'bulk_update', alert_ids: alertIds, ...updates }, headers } }).response;
  },
  discoverFunctions: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/monitor/discover', options: { headers } }).response;
  },
  getMonitorCompliance: async (hours: number = 24) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/monitor/compliance', options: { headers, queryParams: { hours: String(hours) } } }).response;
  },
  getMonitorValue: async (automationId?: string, days: number = 30) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = { days: String(days) };
    if (automationId) queryParams.automation_id = automationId;
    return get({ apiName: API_NAME, path: '/monitor/value', options: { headers, queryParams } }).response;
  },
  importFunction: async (functionName: string) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/monitor/import', options: { body: { function_name: functionName }, headers } }).response;
  },

  // AMY Chat
  chatWithAmy: async (message: string, history: Array<{role: string, content: string}> = []) => {
    const headers = await getAuthHeaders();
    return post({
      apiName: API_NAME,
      path: '/amy/chat',
      options: {
        body: {
          action: 'chat',
          message,
          history
        },
        headers
      }
    }).response;
  },

  // All Requests (across automations)
  // opts.includeClosed -> returns closed/resolved history (for the Resolved/Closed tabs)
  // opts.status        -> returns requests with exactly that status
  // Default (no opts)  -> open/active requests only (fast, common case)
  getAllRequests: async (opts?: { includeClosed?: boolean; status?: string }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (opts?.includeClosed) queryParams.include_closed = 'true';
    if (opts?.status) queryParams.status = opts.status;
    return get({ apiName: API_NAME, path: '/automations/0/requests', options: { headers, queryParams } }).response;
  },

  // Request Statuses
  getRequestStatuses: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/automations/0/requests', options: { headers, queryParams: { type: 'statuses' } } }).response;
  },

  // Feature & Bug Requests
  getAutomationRequests: async (id: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${id}/requests`, options: { headers } }).response;
  },
  createRequest: async (automationId: string, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/${automationId}/requests`, options: { body: data, headers } }).response;
  },
  updateRequest: async (automationId: string, requestId: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/automations/${automationId}/requests/${requestId}`, options: { body: data, headers } }).response;
  },
  deleteRequest: async (automationId: string, requestId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/automations/${automationId}/requests/${requestId}`, options: { headers } }).response;
  },
  bulkUpdateRequests: async (requestIds: number[], updates: { status?: string; assigned_to?: number | null; priority?: string }) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/automations/0/requests', options: { body: { _action: 'bulk_update', request_ids: requestIds, ...updates }, headers } }).response;
  },
  getRequestComments: async (requestId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/requests/${requestId}/comments`, options: { headers } }).response;
  },
  createRequestComment: async (requestId: number, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/requests/${requestId}/comments`, options: { body: data, headers } }).response;
  },

  // Tasks (per-request checklist / pre-deploy items)
  getRequestTasks: async (requestId: number, pendingProd = false) => {
    const headers = await getAuthHeaders();
    const queryParams = pendingProd ? { pending_prod: '1' } : undefined;
    return get({ apiName: API_NAME, path: `/requests/${requestId}/tasks`, options: { headers, queryParams } }).response;
  },
  createRequestTask: async (requestId: number, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/requests/${requestId}/tasks`, options: { body: data, headers } }).response;
  },
  updateRequestTask: async (requestId: number, taskId: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/requests/${requestId}/tasks/${taskId}`, options: { body: data, headers } }).response;
  },
  deleteRequestTask: async (requestId: number, taskId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/requests/${requestId}/tasks/${taskId}`, options: { headers } }).response;
  },
  reorderRequestTasks: async (requestId: number, taskIds: number[]) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/requests/${requestId}/tasks`, options: { body: { _action: 'reorder', task_ids: taskIds }, headers } }).response;
  },

  // Watchers
  getRequestWatchers: async (requestId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/requests/${requestId}/watchers`, options: { headers } }).response;
  },
  addRequestWatcher: async (requestId: number, userId: number) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/requests/${requestId}/watchers`, options: { body: { user_id: userId }, headers } }).response;
  },
  removeRequestWatcher: async (requestId: number, userId: number) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/requests/${requestId}/watchers`, options: { body: { user_id: userId, _action: 'remove' }, headers } }).response;
  },
  getAutomationWatchers: async (automationId: string) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/${automationId}/watchers`, options: { headers } }).response;
  },
  addAutomationWatcher: async (automationId: string, userId: number, watchPreferences?: { request_types: string[] }) => {
    const headers = await getAuthHeaders();
    const body: any = { user_id: userId };
    if (watchPreferences && watchPreferences.request_types.length > 0) {
      body.watch_preferences = watchPreferences;
    }
    return post({ apiName: API_NAME, path: `/automations/${automationId}/watchers`, options: { body, headers } }).response;
  },
  removeAutomationWatcher: async (automationId: string, userId: number) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/${automationId}/watchers`, options: { body: { user_id: userId, _action: 'remove' }, headers } }).response;
  },

  // Time Entries
  getRequestTimeEntries: async (requestId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/automations/0/requests`, options: { headers, queryParams: { type: 'time_entries', request_id: String(requestId) } } }).response;
  },
  createTimeEntry: async (requestId: number, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/automations/0/requests`, options: { body: { ...data, request_id: requestId, _action: 'create_time_entry' }, headers } }).response;
  },

  // Import Sources
  getImportSources: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/integration-configs', options: { headers, queryParams: { table: 'import_source' } } }).response;
  },
  createImportSource: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/automations', options: { body: { ...data, _table: 'import_source' }, headers } }).response;
  },
  getImportLogs: async (sourceId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/integration-configs', options: { headers, queryParams: { table: 'import_log', sqlQueryParams: JSON.stringify({ source_id: sourceId }) } } }).response;
  },

  // Kiro Developer Token
  getKiroToken: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/users/me/token', options: { headers } }).response;
  },
  regenerateKiroToken: async () => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/users/me/token', options: { body: {}, headers } }).response;
  },

  // Deployments
  getDeployments: async (params?: { status?: string; limit?: number }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.limit) queryParams.limit = String(params.limit);
    return get({ apiName: API_NAME, path: '/deployments', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },
  getDeployment: async (id: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/deployments/${id}`, options: { headers } }).response;
  },
  createDeployment: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/deployments', options: { body: data, headers } }).response;
  },
  updateDeployment: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/deployments/${id}`, options: { body: data, headers } }).response;
  },
  deleteDeployment: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/deployments/${id}`, options: { headers } }).response;
  },
  triggerDeployment: async (id: number, data?: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/deployments/${id}/trigger`, options: { body: data || {}, headers } }).response;
  },
  linkDeploymentCommits: async (id: number, commits: any[]) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/deployments/${id}/commits`, options: { body: { commits }, headers } }).response;
  },
  linkDeploymentRequests: async (id: number, requestIds: number[]) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/deployments/${id}/requests`, options: { body: { request_ids: requestIds }, headers } }).response;
  },
  addDeploymentTests: async (id: number, tests: any[]) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/deployments/${id}/tests`, options: { body: { tests }, headers } }).response;
  },
  updateDeploymentTest: async (deploymentId: number, testId: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/deployments/${deploymentId}/tests/${testId}`, options: { body: data, headers } }).response;
  },
  suggestDeploymentTests: async (id: number) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/deployments/${id}/suggest-tests`, options: { body: {}, headers } }).response;
  },
  getPendingDeploymentTasks: async (includeCompleted = false) => {
    const headers = await getAuthHeaders();
    const queryParams = includeCompleted ? { include_completed: 'true' } : undefined;
    return get({ apiName: API_NAME, path: '/deployments/pending-tasks', options: { headers, queryParams } }).response;
  },
  getDeploymentLocks: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/deployments/locks', options: { headers } }).response;
  },
  createDeploymentLock: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/deployments/locks', options: { body: data, headers } }).response;
  },
  releaseDeploymentLock: async (lockId: number, releasedBy: string) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/deployments/locks/${lockId}`, options: { body: { released_by: releasedBy }, headers } }).response;
  },
  nudgeLockHolder: async (lockId: number, senderName?: string) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/deployments/locks/${lockId}/nudge`, options: { body: { sender_name: senderName || 'Someone' }, headers } }).response;
  },

  // Reports
  getReportsSummary: async (days: number = 90) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/reports', options: { headers, queryParams: { days: String(days) } } }).response;
  },
  getAlertsResponseMetrics: async (days: number = 90) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/reports/alerts-response', options: { headers, queryParams: { days: String(days) } } }).response;
  },
  getRequestsPerDeveloper: async (days: number = 90) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/reports/requests-per-dev', options: { headers, queryParams: { days: String(days) } } }).response;
  },
  getAutomationsPerDeveloper: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/reports/automations-per-dev', options: { headers } }).response;
  },
  getRequestsPerAutomation: async (days: number = 90) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/reports/requests-per-auto', options: { headers, queryParams: { days: String(days) } } }).response;
  },

  // Attachments
  getAttachments: async (requestId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/attachments', options: { headers, queryParams: { request_id: String(requestId) } } }).response;
  },
  getUploadUrl: async (data: { file_name: string; file_type: string; request_id: number; comment_id?: number }) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/attachments/upload-url', options: { body: data, headers } }).response;
  },
  confirmAttachment: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/attachments/confirm', options: { body: data, headers } }).response;
  },
  getDownloadUrl: async (attachmentId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/attachments/${attachmentId}/download`, options: { headers } }).response;
  },
  deleteAttachment: async (attachmentId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/attachments/${attachmentId}`, options: { headers } }).response;
  },

  // Outreach Tracker
  getOutreachCampaigns: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/outreach/campaigns', options: { headers } }).response;
  },
  getOutreachCampaign: async (id: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/outreach/campaigns/${id}`, options: { headers } }).response;
  },
  createOutreachCampaign: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/outreach/campaigns', options: { body: data, headers } }).response;
  },
  updateOutreachCampaign: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/outreach/campaigns/${id}`, options: { body: data, headers } }).response;
  },
  deleteOutreachCampaign: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/outreach/campaigns/${id}`, options: { headers } }).response;
  },

  getOutreachContacts: async (teamId?: number) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (teamId) queryParams.team_id = String(teamId);
    return get({ apiName: API_NAME, path: '/outreach/contacts', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },
  getOutreachContact: async (id: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/outreach/contacts/${id}`, options: { headers } }).response;
  },
  createOutreachContact: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/outreach/contacts', options: { body: data, headers } }).response;
  },
  updateOutreachContact: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/outreach/contacts/${id}`, options: { body: data, headers } }).response;
  },
  deleteOutreachContact: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/outreach/contacts/${id}`, options: { headers } }).response;
  },

  getOutreachActivities: async (params?: { contact_id?: number; campaign_id?: number; status?: string }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.contact_id) queryParams.contact_id = String(params.contact_id);
    if (params?.campaign_id) queryParams.campaign_id = String(params.campaign_id);
    if (params?.status) queryParams.status = params.status;
    return get({ apiName: API_NAME, path: '/outreach/activities', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },
  createOutreachActivity: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/outreach/activities', options: { body: data, headers } }).response;
  },
  updateOutreachActivity: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/outreach/activities/${id}`, options: { body: data, headers } }).response;
  },
  deleteOutreachActivity: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/outreach/activities/${id}`, options: { headers } }).response;
  },

  getOutreachOutcomes: async (params?: { campaign_id?: number; contact_id?: number }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.campaign_id) queryParams.campaign_id = String(params.campaign_id);
    if (params?.contact_id) queryParams.contact_id = String(params.contact_id);
    return get({ apiName: API_NAME, path: '/outreach/outcomes', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },
  createOutreachOutcome: async (data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: '/outreach/outcomes', options: { body: data, headers } }).response;
  },
  updateOutreachOutcome: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/outreach/outcomes/${id}`, options: { body: data, headers } }).response;
  },
  deleteOutreachOutcome: async (id: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/outreach/outcomes/${id}`, options: { headers } }).response;
  },

  getOutreachMetrics: async (params?: { campaign_id?: number; days?: number }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.campaign_id) queryParams.campaign_id = String(params.campaign_id);
    if (params?.days) queryParams.days = String(params.days);
    return get({ apiName: API_NAME, path: '/outreach/metrics', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },

  // Outreach Stakeholders
  getOutreachStakeholders: async (campaignId: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/outreach/campaigns/${campaignId}/stakeholders`, options: { headers } }).response;
  },
  createOutreachStakeholder: async (campaignId: number, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/outreach/campaigns/${campaignId}/stakeholders`, options: { body: data, headers } }).response;
  },
  updateOutreachStakeholder: async (campaignId: number, stakeholderId: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/outreach/campaigns/${campaignId}/stakeholders/${stakeholderId}`, options: { body: data, headers } }).response;
  },
  deleteOutreachStakeholder: async (campaignId: number, stakeholderId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/outreach/campaigns/${campaignId}/stakeholders/${stakeholderId}`, options: { headers } }).response;
  },

  // AI Opportunities
  getAiOpportunities: async (params?: { status?: string; impact?: string; accessibility?: string; team_id?: string; tags?: string; q?: string; limit?: number; offset?: number }) => {
    const headers = await getAuthHeaders();
    const queryParams: Record<string, string> = {};
    if (params?.status) queryParams.status = params.status;
    if (params?.impact) queryParams.impact = params.impact;
    if (params?.accessibility) queryParams.accessibility = params.accessibility;
    if (params?.team_id) queryParams.team_id = params.team_id;
    if (params?.tags) queryParams.tags = params.tags;
    if (params?.q) queryParams.q = params.q;
    if (params?.limit != null) queryParams.limit = String(params.limit);
    if (params?.offset != null) queryParams.offset = String(params.offset);
    return get({ apiName: API_NAME, path: '/ai-opportunities', options: { headers, queryParams: Object.keys(queryParams).length ? queryParams : undefined } }).response;
  },
  getAiOpportunityTags: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/ai-opportunities/tags', options: { headers } }).response;
  },
  addAiOpportunityTag: async (id: number, tag: string) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/ai-opportunities/${id}/tags`, options: { body: { tag }, headers } }).response;
  },
  removeAiOpportunityTag: async (id: number, tagId: number) => {
    const headers = await getAuthHeaders();
    return del({ apiName: API_NAME, path: `/ai-opportunities/${id}/tags/${tagId}`, options: { headers } }).response;
  },
  getAiOpportunity: async (id: number) => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: `/ai-opportunities/${id}`, options: { headers } }).response;
  },
  getAiOpportunityStats: async () => {
    const headers = await getAuthHeaders();
    return get({ apiName: API_NAME, path: '/ai-opportunities/stats', options: { headers } }).response;
  },
  updateAiOpportunity: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return patch({ apiName: API_NAME, path: `/ai-opportunities/${id}`, options: { body: data, headers } }).response;
  },
  promoteAiOpportunity: async (id: number, data: any) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/ai-opportunities/${id}/promote`, options: { body: data, headers } }).response;
  },
  addAiOpportunityComment: async (id: number, comment: string) => {
    const headers = await getAuthHeaders();
    return post({ apiName: API_NAME, path: `/ai-opportunities/${id}/comments`, options: { body: { comment }, headers } }).response;
  },
};